import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { t } from "../i18n";
import { usePlayerContext } from "../providers/PlayerProvider";

function getAuthHeaders() {
  const token = localStorage.getItem("fm_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const SOURCE_ICONS = {
  liked: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  favorites: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  temp: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  ),
  playlist: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 17l-3.5 2.5.5-5L2 10l5-.5L9 5l2 4.5 5 .5-3 4.5.5 5z" />
      <circle cx="18" cy="5" r="3" />
      <line x1="18" y1="8" x2="18" y2="21" />
      <circle cx="13" cy="18" r="3" />
    </svg>
  ),
};

export default function PlaylistDetailPage({ lang }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [removingTracks, setRemovingTracks] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [previewId, setPreviewId] = useState(null);

  // Add track modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addStep, setAddStep] = useState("source"); // "source" | "pick-playlist" | "pick-tracks"
  const [addSource, setAddSource] = useState(null);
  const [sourcePlaylists, setSourcePlaylists] = useState([]);
  const [selectedSrcPlaylistId, setSelectedSrcPlaylistId] = useState(null); // eslint-disable-line no-unused-vars
  const [sourceTracks, setSourceTracks] = useState([]);
  const [selectedAddIds, setSelectedAddIds] = useState(new Set());
  const [loadingSource, setLoadingSource] = useState(false);
  const [addingTracks, setAddingTracks] = useState(false);
  const _sourceTracksRef = useRef(null);

  // Audio for preview
  const [previewAudio, setPreviewAudio] = useState(null);

  // Batch download state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchDownloading, setBatchDownloading] = useState(false);

  // Player context
  const { setPlaylist: setPlayerPlaylist, setPlaylistName, setPlaylistSource, playTrack } = usePlayerContext();

  // Cover modal state
  const [showCoverModal, setShowCoverModal] = useState(false);
  const [coverUrlInput, setCoverUrlInput] = useState("");
  const [uploadingCover, setUploadingCover] = useState(false);

  function showToast(message, type = "success") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }

  const fetchPlaylist = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/playlists/${id}`, {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error("Failed to fetch playlist");
      const data = await res.json();
      setPlaylist(data);
      // Flatten PlaylistTrackOut → real Track objects
      const flattened = (data.tracks || []).map(pt => ({
        ...pt.track,
        _ptTrackId: pt.track_id,
        _position: pt.position,
      }));
      setTracks(flattened);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPlaylist();
  }, [fetchPlaylist]);

  // Play track preview
  function playPreview(track) {
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.src = "";
      if (previewId === track.id) {
        setPreviewId(null);
        setPreviewAudio(null);
        return;
      }
    }
    const audio = new Audio(track.url || track.audio_url);
    audio.volume = 0.8;
    audio.addEventListener("ended", () => {
      setPreviewId(null);
      setPreviewAudio(null);
    });
    audio.addEventListener("error", () => {
      setPreviewId(null);
      setPreviewAudio(null);
    });
    audio.play().catch(() => {
      setPreviewId(null);
      setPreviewAudio(null);
    });
    setPreviewAudio(audio);
    setPreviewId(track.id);
  }

  // Remove track from playlist
  async function handleRemoveTrack(trackId) {
    setRemovingTracks((prev) => new Set(prev).add(trackId));
    try {
      const res = await fetch(`/api/playlists/${id}/tracks/${trackId}`, {
        method: "DELETE",
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error("Remove failed");
      setTracks((prev) => prev.filter((t) => t.id !== trackId));
    } catch (e) {
      console.error("Remove track error:", e);
    } finally {
      setRemovingTracks((prev) => {
        const next = new Set(prev);
        next.delete(trackId);
        return next;
      });
    }
  }

  // Share playlist
  async function handleShare() {
    const url = `${window.location.origin}/immersive_player?playlist=${playlist.id || id}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast(t(lang, "sharePlaylistCopied"), "success");
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      showToast(t(lang, "sharePlaylistCopied"), "success");
    }
  }

  // Batch download
  async function handleBatchDownload() {
    const token = localStorage.getItem("fm_token");
    if (!token) {
      window.dispatchEvent(new CustomEvent('open-login-modal'));
      return;
    }
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBatchDownloading(true);
    try {
      const res = await fetch("/api/tracks/batch-download", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ track_ids: ids }),
      });
      if (res.status === 403) {
        showToast(t(lang, "downloadLimitExceeded"), "error");
        return;
      }
      if (!res.ok) throw new Error(t(lang, "downloadFailed"));
      const data = await res.json();
      const urls = data.urls || [];
      urls.forEach((url, i) => {
        setTimeout(() => {
          const a = document.createElement("a");
          a.href = url;
          a.download = `track_${ids[i]}`;
          a.click();
        }, i * 500);
      });
      setSelectedIds(new Set());
      setSelectMode(false);
      showToast(t(lang, "downloadStarted"), "success");
    } catch (e) {
      console.error("Batch download error:", e);
      showToast(e.message, "error");
    } finally {
      setBatchDownloading(false);
    }
  }

  function toggleSelectMode() {
    if (selectMode) {
      setSelectMode(false);
      setSelectedIds(new Set());
    } else {
      setSelectMode(true);
    }
  }

  function toggleSelectTrack(trackId) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(trackId)) next.delete(trackId);
      else next.add(trackId);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === tracks.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(tracks.map(t => t.id)));
    }
  }

  // Delete whole playlist
  async function handleDeletePlaylist() {
    if (!confirm(t(lang, "deletePlaylistConfirm"))) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/playlists/${id}`, {
        method: "DELETE",
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error("Delete failed");
      showToast(t(lang, "playlistDeleted"), "success");
      setTimeout(() => navigate("/playlists"), 500);
    } catch (e) {
      console.error("Delete playlist error:", e);
      setDeleting(false);
    }
  }

  // ── Cover Image ──

  async function handleCoverUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`/api/playlists/${id}/cover`, {
        method: "POST",
        headers: { ...getAuthHeaders() },
        body: formData,
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setPlaylist((prev) => ({ ...prev, cover_url: data.cover_url }));
      showToast(t(lang, "coverUpdated"), "success");
      setShowCoverModal(false);
    } catch (e) {
      console.error("Cover upload error:", e);
      showToast(e.message, "error");
    } finally {
      setUploadingCover(false);
      if (e.target) e.target.value = "";
    }
  }

  async function handleCoverUrlSubmit() {
    const url = coverUrlInput.trim();
    if (!url) return;
    setUploadingCover(true);
    try {
      const res = await fetch(`/api/playlists/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ cover_url: url }),
      });
      if (!res.ok) throw new Error("Update failed");
      const data = await res.json();
      setPlaylist((prev) => ({ ...prev, cover_url: data.cover_url }));
      showToast(t(lang, "coverUpdated"), "success");
      setShowCoverModal(false);
      setCoverUrlInput("");
    } catch (e) {
      console.error("Cover URL error:", e);
      showToast(e.message, "error");
    } finally {
      setUploadingCover(false);
    }
  }

  // ── Add Track Modal Logic ──

  function openAddModal() {
    setAddStep("source");
    setAddSource(null);
    setSelectedSrcPlaylistId(null);
    setSourceTracks([]);
    setSelectedAddIds(new Set());
    setShowAddModal(true);
  }

  function closeAddModal() {
    setShowAddModal(false);
    setAddStep("source");
    setAddSource(null);
    setSelectedSrcPlaylistId(null);
    setSourceTracks([]);
    setSelectedAddIds(new Set());
    setLoadingSource(false);
  }

  function getExistingIds() {
    return new Set(tracks.map((t) => t.id));
  }

  async function handleSourcePick(source) {
    setAddSource(source);
    setLoadingSource(true);
    setSourceTracks([]);
    setSelectedAddIds(new Set());

    try {
      if (source === "liked") {
        const res = await fetch("/api/actions/liked/details", {
          headers: { ...getAuthHeaders() },
        });
        if (!res.ok) throw new Error("Failed to fetch liked tracks");
        const data = await res.json();
        const items = data.map((d) => ({
          ...d.track,
          _scene: d.atmosphere,
        }));
        setSourceTracks(items);
        setAddStep("pick-tracks");
      } else if (source === "favorites") {
        const res = await fetch("/api/favorites/list/details", {
          headers: { ...getAuthHeaders() },
        });
        if (!res.ok) throw new Error("Failed to fetch favorites");
        const data = await res.json();
        const items = data.map((d) => ({
          ...d.track,
          _scene: d.atmosphere,
        }));
        setSourceTracks(items);
        setAddStep("pick-tracks");
      } else if (source === "temp") {
        // Read temp playlist from localStorage
        try {
          const raw = localStorage.getItem("fm_temp_playlist");
          const items = raw ? JSON.parse(raw) : [];
          setSourceTracks(items);
        } catch {
          setSourceTracks([]);
        }
        setAddStep("pick-tracks");
      } else if (source === "playlist") {
        // Step 1: show list of other playlists
        const res = await fetch("/api/playlists/", {
          headers: { ...getAuthHeaders() },
        });
        if (!res.ok) throw new Error("Failed to fetch playlists");
        const data = await res.json();
        const others = (data.playlists || []).filter((p) => p.id !== id);
        setSourcePlaylists(others);
        setAddStep("pick-playlist");
      }
    } catch (e) {
      console.error("Source fetch error:", e);
      showToast(e.message, "error");
      setAddStep("source");
    } finally {
      setLoadingSource(false);
    }
  }

  async function handlePlaylistPick(playlistId) {
    setSelectedSrcPlaylistId(playlistId);
    setLoadingSource(true);
    try {
      const res = await fetch(`/api/playlists/${playlistId}`, {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error("Failed to fetch playlist");
      const data = await res.json();
      const flattened = (data.tracks || []).map((pt) => ({
        ...pt.track,
        _scene: pt.track?.category || "",
      }));
      setSourceTracks(flattened);
      setAddStep("pick-tracks");
    } catch (e) {
      console.error("Playlist fetch error:", e);
      showToast(e.message, "error");
    } finally {
      setLoadingSource(false);
    }
  }

  function toggleAddSelection(trackId) {
    setSelectedAddIds((prev) => {
      const next = new Set(prev);
      if (next.has(trackId)) {
        next.delete(trackId);
      } else {
        next.add(trackId);
      }
      return next;
    });
  }

  async function handleAddTracks() {
    if (selectedAddIds.size === 0) return;
    setAddingTracks(true);
    try {
      const ids = Array.from(selectedAddIds);
      const res = await fetch(`/api/playlists/${id}/tracks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ track_ids: ids }),
      });
      if (!res.ok) throw new Error("Add tracks failed");
      // Refresh the playlist
      await fetchPlaylist();
      showToast(
        t(lang, "addSelected_plural").replace("{count}", ids.length),
        "success"
      );
      closeAddModal();
    } catch (e) {
      console.error("Add tracks error:", e);
      showToast(e.message, "error");
    } finally {
      setAddingTracks(false);
    }
  }

  // Clean up audio on unmount
  useEffect(() => {
    return () => {
      if (previewAudio) {
        previewAudio.pause();
        previewAudio.src = "";
      }
    };
  }, [previewAudio]);

  // Fallback: stop preview on track removal
  useEffect(() => {
    if (previewId && !tracks.find((t) => t.id === previewId)) {
      if (previewAudio) {
        previewAudio.pause();
        previewAudio.src = "";
      }
      setPreviewId(null);
      setPreviewAudio(null);
    }
  }, [tracks, previewId, previewAudio]);

  // ── Render Source Picker ──
  function renderSourcePicker() {
    const sources = [
      { key: "liked", label: t(lang, "fromLiked"), icon: SOURCE_ICONS.liked },
      { key: "favorites", label: t(lang, "fromFavorites"), icon: SOURCE_ICONS.favorites },
      { key: "temp", label: t(lang, "fromPlayingList"), icon: SOURCE_ICONS.temp },
      { key: "playlist", label: t(lang, "fromPlaylist"), icon: SOURCE_ICONS.playlist },
    ];
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-500">{t(lang, "addTracksSource")}</p>
        <div className="grid grid-cols-2 gap-3">
          {sources.map((s) => (
            <button
              key={s.key}
              onClick={() => handleSourcePick(s.key)}
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition text-gray-700 hover:text-indigo-700"
            >
              <div className="w-10 h-10 flex items-center justify-center text-indigo-500">
                {s.icon}
              </div>
              <span className="text-sm font-medium">{s.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Render Playlist Picker (for "fromPlaylist" source) ──
  function renderPlaylistPicker() {
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-500">{t(lang, "selectPlaylistFirst")}</p>
        {sourcePlaylists.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="mx-auto mb-3 opacity-40">
              <path d="M8 17l-3.5 2.5.5-5L2 10l5-.5L9 5l2 4.5 5 .5-3 4.5.5 5z" />
            </svg>
            <p>没有其他歌单</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {sourcePlaylists.map((p) => (
              <button
                key={p.id}
                onClick={() => handlePlaylistPick(p.id)}
                className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50 transition"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-500 text-sm font-bold">
                  ♪
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                  <p className="text-xs text-gray-400">
                    {p.track_count || (p.tracks && p.tracks.length) || 0} 首
                  </p>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-300 flex-shrink-0">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Render Track Picker ──
  function renderTrackPicker() {
    const existingIds = getExistingIds();

    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-500">
          {t(lang, "selectTracks")}（{t(lang, "selectedCount")}: {selectedAddIds.size}）
        </p>
        {loadingSource ? (
          <div className="flex flex-col items-center py-10 text-gray-400">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin mb-2" />
            <span className="text-sm">{t(lang, "loadingTracks")}</span>
          </div>
        ) : sourceTracks.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="mx-auto mb-3 opacity-40">
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
            <p className="text-sm">{t(lang, "noTracks")}</p>
          </div>
        ) : (
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {sourceTracks.map((track) => {
              const isExisting = existingIds.has(track.id);
              const isSelected = selectedAddIds.has(track.id);
              const trackName = track.name_zh || track.name_en || track.name || track.id;
              const sceneName = track._scene || track.category || "";

              return (
                <div
                  key={track.id}
                  onClick={() => {
                    if (!isExisting) toggleAddSelection(track.id);
                  }}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition cursor-pointer ${
                    isExisting
                      ? "opacity-40 cursor-not-allowed bg-gray-50"
                      : isSelected
                      ? "bg-indigo-50 border border-indigo-200"
                      : "hover:bg-gray-50 border border-transparent"
                  }`}
                >
                  {/* Checkbox */}
                  <div className={`w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition ${
                    isExisting
                      ? "border-gray-200 bg-gray-100"
                      : isSelected
                      ? "border-indigo-500 bg-indigo-500"
                      : "border-gray-300"
                  }`}>
                    {isExisting ? (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="3">
                        <line x1="18" y1="6" x2="6" y2="18" />
                      </svg>
                    ) : isSelected ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : null}
                  </div>

                  {/* Track info */}
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-800 truncate block">
                      {trackName}
                    </span>
                    {sceneName && (
                      <span className="text-xs text-gray-400">{sceneName}</span>
                    )}
                  </div>

                  {/* Already in playlist badge */}
                  {isExisting && (
                    <span className="text-xs text-gray-400 flex-shrink-0 px-2 py-0.5 bg-gray-100 rounded-full">
                      已添加
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Render Add Track Modal ──
  function renderAddModal() {
    if (!showAddModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={closeAddModal}
        />
        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <button
              onClick={() => {
                if (addStep === "pick-tracks" && addSource === "playlist") {
                  setAddStep("pick-playlist");
                  setSelectedAddIds(new Set());
                } else if (addStep === "pick-playlist" || addStep === "pick-tracks") {
                  setAddStep("source");
                  setAddSource(null);
                  setSelectedAddIds(new Set());
                } else {
                  closeAddModal();
                }
              }}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-base font-semibold text-gray-900">
              {addStep === "source" && t(lang, "addTracksTitle")}
              {addStep === "pick-playlist" && t(lang, "fromPlaylist")}
              {addStep === "pick-tracks" && t(lang, "selectTracks")}
            </h2>
            <button
              onClick={closeAddModal}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {addStep === "source" && renderSourcePicker()}
            {addStep === "pick-playlist" && renderPlaylistPicker()}
            {addStep === "pick-tracks" && renderTrackPicker()}
          </div>

          {/* Footer action button */}
          {addStep === "pick-tracks" && selectedAddIds.size > 0 && (
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={handleAddTracks}
                disabled={addingTracks}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition ${
                  addingTracks
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg"
                }`}
              >
                {addingTracks ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {t(lang, "adding") || "添加中..."}
                  </>
                ) : (
                  t(lang, "addSelected_plural").replace("{count}", selectedAddIds.size)
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{playlist ? `${playlist.name} — FreeMusic.Top` : 'FreeMusic.Top'}</title>
        <meta name="description" content={playlist?.description || 'Scene music playlist on FreeMusic.Top'} />
      </Helmet>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/playlists")}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900 truncate">
              {playlist ? playlist.name : t(lang, "playlistDetail")}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
              title={t(lang, "sharePlaylist")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              <span className="hidden sm:inline">{t(lang, "sharePlaylist")}</span>
            </button>
            <button
              onClick={handleDeletePlaylist}
              disabled={deleting}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition ${
                deleting
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "text-red-500 hover:text-red-600 hover:bg-red-50"
              }`}
            >
              {deleting ? (
                <div className="w-4 h-4 border-2 border-red-300 border-t-red-500 rounded-full animate-spin" />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              )}
              <span className="hidden sm:inline">{t(lang, "deleteConfirm")}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin mb-3" />
            <span className="text-sm">{t(lang, "loading")}</span>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-red-500">
            <div className="text-lg mb-2">⚠️ {error}</div>
            <button
              onClick={fetchPlaylist}
              className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition"
            >
              {t(lang, "retry") || "Retry"}
            </button>
          </div>
        )}

        {/* Playlist info */}
        {!loading && !error && playlist && (
          <>
            {/* Cover image */}
            <div className="relative mb-6 group">
              {playlist.cover_url ? (
                <div className="relative w-full h-48 rounded-2xl overflow-hidden bg-gradient-to-br from-indigo-100 to-purple-100 shadow-md">
                  <img
                    src={playlist.cover_url}
                    alt={playlist.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition flex items-center justify-center" style={{ display: "none" }}>
                    <span className="text-white text-sm font-medium">{t(lang, "changeCover")}</span>
                  </div>
                </div>
              ) : (
                <div className="w-full h-48 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/50 transition"
                  onClick={() => { setCoverUrlInput(""); setShowCoverModal(true); }}
                >
                  <div className="flex flex-col items-center gap-2 text-gray-400">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    <span className="text-sm">{t(lang, "addCover")}</span>
                  </div>
                </div>
              )}
              {playlist.cover_url && (
                <button
                  onClick={() => { setCoverUrlInput(""); setShowCoverModal(true); }}
                  className="absolute top-3 right-3 px-3 py-1.5 bg-white/80 backdrop-blur-sm rounded-lg text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-white/90 border border-white/50 shadow-sm transition opacity-0 group-hover:opacity-100"
                >
                  {t(lang, "changeCover")}
                </button>
              )}
            </div>

            {/* Meta info */}
            <div className="mb-6">
              {playlist.description && (
                <p className="text-sm text-gray-500 mb-2">{playlist.description}</p>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-sm text-gray-400">
                  <div className="flex items-center gap-1.5">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18V5l12-2v13" />
                      <circle cx="6" cy="18" r="3" />
                      <circle cx="18" cy="16" r="3" />
                    </svg>
                    <span>{tracks.length} {t(lang, "tracksInPlaylist")}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Select mode toggle */}
                  {tracks.length > 0 && (
                    <button
                      onClick={toggleSelectMode}
                      className={`text-xs px-3 py-1.5 rounded-lg border transition ${
                        selectMode
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "border-gray-300 text-gray-600 hover:border-indigo-400"
                      }`}
                    >
                      {selectMode ? t(lang, "cancel") : t(lang, "selectTracks")}
                    </button>
                  )}
                  {/* Add tracks button */}
                  <button
                    onClick={openAddModal}
                    className="flex items-center gap-2 px-4 py-2 border border-indigo-200 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 text-sm font-medium rounded-xl transition"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    <span>{t(lang, "addTracks")}</span>
                  </button>
                  {/* Play all button */}
                  {tracks.length > 0 && (
                    <button
                      onClick={() => {
                        setPlayerPlaylist(tracks);
                        setPlaylistName(playlist?.name || "");
                        setPlaylistSource("playlist");
                        playTrack(tracks[0], null);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition shadow-md hover:shadow-lg"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                      <span>{t(lang, "playAll")}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Batch download bar (select mode) */}
            {selectMode && (
              <div className="mb-4 px-4 py-3 bg-indigo-50 rounded-xl border border-indigo-200 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <button
                    onClick={toggleSelectAll}
                    className="text-xs px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-100 transition"
                  >
                    {selectedIds.size === tracks.length ? t(lang, "deselectAll") : t(lang, "selectAll")}
                  </button>
                  <span className="text-sm text-indigo-700 font-medium">
                    {t(lang, "selectedCount")}: {selectedIds.size}
                  </span>
                </div>
                <button
                  onClick={handleBatchDownload}
                  disabled={batchDownloading || selectedIds.size === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition disabled:opacity-50"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    {batchDownloading ? t(lang, "downloadInProgress") : `${t(lang, "batchDownload")} (${selectedIds.size})`}
                  </button>
              </div>
            )}

            {/* Track list */}
            {tracks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="mb-4 opacity-40">
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
                <p className="text-base mb-4">{t(lang, "emptyPlaylist")}</p>
                <button
                  onClick={openAddModal}
                  className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition shadow-md hover:shadow-lg"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  <span>{t(lang, "addTracks")}</span>
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="divide-y divide-gray-100">
                  {tracks.map((track, index) => {
                    const scene = track.scene || track.atmosphere || {};
                    const emoji = scene.emoji || "🎵";
                    const sceneName = scene.name || scene.nameEn || "";
                    const trackName = track.name_zh || track.name_en || track.name || `${t(lang, "track")} ${index + 1}`;
                    const typeLabel = track.type === "vocal" || track.type === "songs"
                      ? t(lang, "vocal")
                      : track.type === "instrumental"
                      ? t(lang, "instrumental")
                      : "";

                    return (
                      <div
                        key={track.id || index}
                        className={`flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition group ${
                          selectMode && selectedIds.has(track.id) ? "bg-indigo-50 border border-indigo-200 rounded-lg mx-2" : ""
                        }`}
                      >
                        {/* Checkbox (select mode) */}
                        {selectMode ? (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(track.id)}
                            onChange={() => toggleSelectTrack(track.id)}
                            className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer flex-shrink-0"
                          />
                        ) : (
                          /* Play button */
                          <button
                            onClick={() => playPreview(track)}
                            className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg transition shadow-sm ${
                              previewId === track.id
                                ? "bg-indigo-600 text-white"
                                : "bg-indigo-100 hover:bg-indigo-200 text-indigo-600 hover:text-indigo-700"
                            }`}
                            title={t(lang, "play")}
                          >
                          {previewId === track.id ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <rect x="6" y="4" width="4" height="16" rx="1" />
                              <rect x="14" y="4" width="4" height="16" rx="1" />
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <polygon points="5 3 19 12 5 21 5 3" />
                            </svg>
                          )}
                        </button>
                        )}
                        {/* Track info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{emoji}</span>
                            <span className="text-base font-medium text-gray-800 truncate">
                              {trackName}
                            </span>
                          </div>
                          <div className="text-sm text-gray-400 mt-0.5">
                            {sceneName}
                            {typeLabel && <span className="ml-2">· {typeLabel}</span>}
                          </div>
                        </div>

                        {/* Remove button (hidden in select mode) */}
                        {!selectMode && (
                        <button
                          onClick={() => handleRemoveTrack(track.id)}
                          disabled={removingTracks.has(track.id)}
                          className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded text-gray-300 hover:text-red-400 hover:bg-red-50 transition opacity-0 group-hover:opacity-100 ${
                            removingTracks.has(track.id) ? "opacity-50 pointer-events-none" : ""
                          }`}
                          title={t(lang, "removeFromPlaylist")}
                        >
                          {removingTracks.has(track.id) ? (
                            <div className="w-3.5 h-3.5 border-2 border-red-300 border-t-red-500 rounded-full animate-spin" />
                          ) : (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          )}
                        </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Cover Modal */}
      {showCoverModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCoverModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {playlist?.cover_url ? t(lang, "changeCover") : t(lang, "addCover")}
            </h2>

            {/* Upload file */}
            <label className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-dashed border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 cursor-pointer transition mb-4">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-gray-400">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span className="text-sm font-medium text-gray-600">{t(lang, "uploadFromDevice")}</span>
              <span className="text-xs text-gray-400">JPEG, PNG, WebP</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleCoverUpload}
                disabled={uploadingCover}
              />
            </label>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 font-medium">{t(lang, "or")}</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* URL input */}
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="https://example.com/cover.jpg"
                value={coverUrlInput}
                onChange={(e) => setCoverUrlInput(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 transition"
                onKeyDown={(e) => e.key === "Enter" && handleCoverUrlSubmit()}
              />
              <button
                onClick={handleCoverUrlSubmit}
                disabled={uploadingCover || !coverUrlInput.trim()}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  uploadingCover || !coverUrlInput.trim()
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700 text-white"
                }`}
              >
                {uploadingCover ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  t(lang, "confirm")
                )}
              </button>
            </div>

            {/* Cancel */}
            <button
              onClick={() => setShowCoverModal(false)}
              className="mt-4 w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition"
            >
              {t(lang, "cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Add Track Modal */}
      {renderAddModal()}

      {/* Toast */}
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[999] animate-fade-in pointer-events-none">
          <div className={`px-6 py-3 rounded-lg shadow-lg text-sm font-medium backdrop-blur-sm ${
            toast.type === "success" ? "bg-green-600/90 text-white" :
            toast.type === "error" ? "bg-red-600/90 text-white" :
            "bg-yellow-600/90 text-white"
          }`}>
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
}
